import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Area, Err } from '../ui.js';
import { captureExtract, captureCommit, ApiError } from '../api.js';
import type { CaptureResult } from '../api.js';

// Paste a thread or an invite; get back an account, its people, and a deal.
// The Outlook-plugin move: the value is not deciding whether it matters — Kyle
// already decided that by pasting it — the value is not typing it all in.
//
// Everything is a checkbox and nothing commits until he says so. Rows that
// already exist are shown as existing and ticked OFF by default: re-capturing a
// thread should link to the account, not clone it.

export function Capture({ onClose, onDone }: {
  onClose: () => void; onDone?: (slug: string) => void;
}) {
  const [text, setText] = useState('');
  const [r, setR] = useState<CaptureResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState({ contacts: new Set<number>(), deal: true, activity: true });
  const nav = useNavigate();

  async function extract() {
    setBusy(true); setError(null);
    try {
      const out = await captureExtract(text);
      setR(out);
      // Pre-tick the people who are NOT already in the CRM.
      setPick({
        contacts: new Set(out.contacts.map((c, i) => (c.existing ? -1 : i)).filter((i) => i >= 0)),
        deal: Boolean(out.deal), activity: true,
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function commit() {
    if (!r) return;
    setBusy(true); setError(null);
    try {
      const out = await captureCommit({
        company: r.company.existing_id
          ? { id: r.company.existing_id }
          : { name: r.company.name, domain: r.company.domain ?? undefined,
              description: r.company.description },
        contacts: r.contacts.filter((_, i) => pick.contacts.has(i))
          .map((c) => ({ name: c.name, email: c.email, title: c.title })),
        deal: pick.deal && r.deal ? r.deal : null,
        activity: pick.activity ? r.activity : null,
      });
      onDone?.(String(out.company.slug));
      nav(`/companies/${out.company.slug}`);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  const toggle = (i: number) => setPick((p) => {
    const n = new Set(p.contacts);
    if (n.has(i)) n.delete(i); else n.add(i);
    return { ...p, contacts: n };
  });

  return (
    <Drawer
      title="Capture from email or invite"
      subtitle="Paste a thread or a calendar invite — it becomes an account, contacts and a deal"
      wide
      onClose={onClose}
      footer={r
        ? <>
            <button className="ghost" onClick={() => setR(null)}>Back</button>
            <button className="primary" disabled={busy} onClick={() => void commit()}>
              {busy ? 'Creating…' : 'Create records'}
            </button>
          </>
        : <button className="primary" disabled={busy || text.trim().length < 20}
            onClick={() => void extract()}>
            {busy ? 'Reading…' : 'Read it'}
          </button>}
    >
      <Err>{error}</Err>

      {!r && (
        <>
          <Area label="Paste the message or invite" rows={16} value={text} onChange={setText}
            placeholder={'Paste the whole thing — headers, signatures, attendee list.\n\n'
              + 'Calendar invites work best: the attendee list is the buying committee, '
              + 'with titles and addresses already in it.'} />
          <p className="fieldhint">
            Nothing is created until you review it. Titles and amounts are only taken from
            the text — never guessed.
          </p>
        </>
      )}

      {r && (
        <>
          <p className="meta">Read as: <strong>{r.kind.replace('_', ' ')}</strong></p>

          <section className="capsec">
            <h4>Account</h4>
            <div className="row">
              <span className="rname">{r.company.name}</span>
              {r.company.domain && <span className="meta">{r.company.domain}</span>}
              <span className={`flag ${r.company.existing ? 'flag-ok' : 'flag-warn'}`}>
                {r.company.existing ? 'already in CRM — will link' : 'new'}
              </span>
            </div>
          </section>

          <section className="capsec">
            <h4>People ({r.contacts.length})</h4>
            {r.contacts.length === 0 && <p className="meta">Nobody found.</p>}
            {r.contacts.map((c, i) => (
              <label className="row caprow" key={`${c.name}-${i}`}>
                <input type="checkbox" checked={pick.contacts.has(i)} onChange={() => toggle(i)} />
                <span className="rname">{c.name}</span>
                <span className="meta">{c.title ?? '—'}</span>
                <span className="meta">{c.email ?? 'no address'}</span>
                {c.existing && <span className="flag flag-ok">exists</span>}
              </label>
            ))}
          </section>

          {r.deal && (
            <section className="capsec">
              <h4>Deal</h4>
              <label className="row caprow">
                <input type="checkbox" checked={pick.deal}
                  onChange={() => setPick((p) => ({ ...p, deal: !p.deal }))} />
                <span className="rname">{r.deal.name}</span>
                <span className="meta">{r.deal.stage}</span>
                {r.deal.amount ? <span className="meta">${r.deal.amount.toLocaleString()}</span> : null}
              </label>
            </section>
          )}

          <section className="capsec">
            <h4>Activity</h4>
            <label className="row caprow">
              <input type="checkbox" checked={pick.activity}
                onChange={() => setPick((p) => ({ ...p, activity: !p.activity }))} />
              <span className="rname">{r.activity.summary}</span>
              <span className="meta">{r.activity.kind}</span>
            </label>
          </section>

          {/* What it could not work out. Cheaper to read than to discover later. */}
          {r.notes && <p className="warnbox">{r.notes}</p>}
        </>
      )}
    </Drawer>
  );
}
